import type { AuthError, User } from '@supabase/supabase-js'
import { supabase } from '@/shared/supabase/client'
import { passkeyAuth, type PasskeySdkItem } from '@/shared/supabase/passkeyAuthClient'
import { supabaseConfig } from '@/shared/config/supabase'
import { AppError, toAppError } from '@/shared/lib/appError'
import type { PasskeyRecord } from '@/shared/api/passkeyTypes'
import {
  createPasskeyCredential,
  deserializePasskeyCreationOptions,
  serializePasskeyCreationResponse,
} from '@/shared/lib/platform/passkeyCeremony'
import {
  ensureNativePasskeyBridge,
  isPasskeyRpIdCompatible,
  isPasskeySupported,
  refreshPasskeyCapability,
} from '@/shared/lib/platform/passkeySupport'
import { finishOAuthCallback as finishOAuthCallbackImpl, signInWithGoogleOAuth } from '@/shared/api/authApiGoogleOAuth'

export { AppError as ApiError }

export interface AuthUser {
  id: string
  name: string
  email: string
  role: 'doctor' | 'patient' | 'admin'
  crm?: string
  specialties?: string[]
  phone1?: string
  phone2?: string
  printName?: string
  contactEmail?: string
  personType?: 'pf' | 'pj'
  cpfCnpj?: string
  pis?: string
  tradeName?: string
  especialidades?: string[]
  telefone1?: string
  telefone2?: string
  avatarUrl?: string
  isSystemAdmin: boolean
  onboardingCompleted: boolean
  emailConfirmed: boolean
}

interface OkResponse {
  ok: boolean
}

export interface OnboardingPayload {
  role: 'doctor' | 'patient'
  crm?: string
  especialidades?: string[]
  telefone1?: string
  telefone2?: string
}

const isBrowser = typeof window !== 'undefined'

const getAuthRedirectTo = (path: string): string | undefined => {
  if (!isBrowser) return undefined
  const fromEnv = (import.meta.env.VITE_AUTH_SITE_URL as string | undefined)?.trim()
  const origin = fromEnv ? fromEnv.replace(/\/$/, '') : window.location.origin
  return `${origin}${path}`
}

const normalizeEmailInput = (value: string): string => value.trim().toLowerCase()

const ensureSupabaseConfigured = () => {
  if (!supabaseConfig.isConfigured) {
    throw new AppError(
      'Supabase não configurado. Defina VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY.',
      { status: 500, code: 'NOT_CONFIGURED' },
    )
  }
}

const toApiError = (error: AuthError, fallbackMessage: string, fallbackStatus = 400): AppError =>
  toAppError(error, fallbackMessage, fallbackStatus)

/** Mapeia mensagens de erro do Supabase Auth para pt-BR. */
const toAuthErrorMessage = (error: AuthError, fallback: string): string => {
  const msg = (error?.message ?? '').toLowerCase()
  if (
    msg.includes('invalid login credentials') ||
    msg.includes('invalid credentials') ||
    msg.includes('invalid grant') ||
    msg.includes('user not found')
  ) {
    return 'Email ou senha inválidos.'
  }
  if (msg.includes('email not confirmed')) {
    return 'Confirme seu email antes de fazer login.'
  }
  if (msg.includes('too many requests') || msg.includes('rate limit')) {
    return 'Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente novamente.'
  }
  if (
    msg.includes('confirmation') &&
    (msg.includes('email') || msg.includes('unexpected_failure') || msg.includes('error sending'))
  ) {
    return 'Não foi possível enviar o email de confirmação. Configure SMTP em Supabase (Authentication > Email) ou desative a confirmação em Authentication > Providers > Email.'
  }
  if (msg.includes('error sending') || msg.includes('unexpected_failure')) {
    return 'Falha ao enviar email. Configure SMTP em Supabase (Authentication > Email) ou desative a confirmação de email para desenvolvimento.'
  }
  if (msg.includes('user already registered') || msg.includes('already been registered')) {
    return 'Este email já está cadastrado. Faça login ou use a opção "Esqueci minha senha".'
  }
  if (msg.includes('password') && msg.includes('weak')) {
    return 'Senha muito fraca. Use pelo menos 8 caracteres com letras e números.'
  }
  return fallback
}

const mapPasskeyRecord = (item: PasskeySdkItem): PasskeyRecord => ({
  id: item.id,
  friendlyName: item.friendly_name,
  createdAt: item.created_at,
  lastUsedAt: item.last_used_at,
})

const readPasskeyErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined
  const code = (error as { code?: unknown }).code
  return typeof code === 'string' ? code : undefined
}

const readPasskeyErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string') return message
  }
  return ''
}

const readPasskeyErrorName = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined
  const name = (error as { name?: unknown }).name
  return typeof name === 'string' ? name : undefined
}

type PasskeyErrorContext = {
  codes: string[]
  names: string[]
  messages: string[]
}

const enqueuePasskeyErrorLinks = (error: unknown, queue: unknown[]): void => {
  if (!error || typeof error !== 'object') return
  const record = error as { cause?: unknown; originalError?: unknown }
  if (record.cause) queue.push(record.cause)
  if (record.originalError) queue.push(record.originalError)
}

/** Percorre a cadeia de erros (cause / originalError) do SDK WebAuthn. */
export const collectPasskeyErrorContext = (error: unknown): PasskeyErrorContext => {
  const codes: string[] = []
  const names: string[] = []
  const messages: string[] = []
  const queue: unknown[] = [error]
  const seen = new Set<unknown>()

  while (queue.length > 0 && seen.size < 10) {
    const current = queue.shift()
    if (!current || seen.has(current)) continue
    seen.add(current)

    const code = readPasskeyErrorCode(current)?.toLowerCase()
    if (code) codes.push(code)

    const name = readPasskeyErrorName(current)?.toLowerCase()
    if (name) names.push(name)

    const msg = readPasskeyErrorMessage(current).toLowerCase()
    if (msg) messages.push(msg)

    if (current instanceof AppError) {
      if (current.cause) queue.push(current.cause)
      continue
    }

    enqueuePasskeyErrorLinks(current, queue)
  }

  return { codes, names, messages }
}

const isInternalWebAuthnAbort = (ctx: PasskeyErrorContext): boolean =>
  ctx.messages.some((message) => message.includes('cancelling existing webauthn api call'))

/** Erro do usuário ao fechar/cancelar o prompt do sistema (não é falha técnica). */
export const isPasskeyUserCancellation = (error: unknown): boolean => {
  if (error instanceof AppError && error.code === 'PASSKEY_CANCELLED') return true

  const ctx = collectPasskeyErrorContext(error)
  if (isInternalWebAuthnAbort(ctx)) return false

  if (ctx.codes.includes('error_ceremony_aborted')) return true
  if (ctx.names.includes('notallowederror')) return true

  const combined = ctx.messages.join(' ')
  if (
    combined.includes('not allowed') ||
    combined.includes('timed out or was not allowed') ||
    combined.includes('operation either timed out') ||
    combined.includes('notallowederror')
  ) {
    return true
  }

  if (combined.includes('abort') || combined.includes('cancel')) {
    return true
  }

  return false
}

/** Mapeia erros de passkey/WebAuthn para mensagens pt-BR amigáveis. */
export const toPasskeyErrorMessage = (error: unknown, fallback: string): string => {
  if (isPasskeyUserCancellation(error)) {
    return 'Operação cancelada.'
  }

  const ctx = collectPasskeyErrorContext(error)
  const combinedCodes = ctx.codes.join(' ')
  const combinedNames = ctx.names.join(' ')
  const combinedMessages = ctx.messages.join(' ')

  if (isInternalWebAuthnAbort(ctx)) {
    return 'Outra operação de chave de acesso estava em andamento. Aguarde e tente novamente.'
  }

  if (
    combinedCodes.includes('error_invalid_rp_id') ||
    combinedMessages.includes('invalid for this domain')
  ) {
    return 'Chaves de acesso só funcionam em app.synap.med.br. Abra o Synap nesse endereço (ambientes de preview não são compatíveis).'
  }

  if (combinedCodes.includes('error_invalid_domain')) {
    return 'O domínio atual não é válido para chaves de acesso. Abra o Synap em app.synap.med.br.'
  }

  if (combinedCodes.includes('error_authenticator_previously_registered')) {
    return 'Esta chave já está cadastrada neste dispositivo.'
  }

  if (
    combinedCodes.includes('error_authenticator_missing_discoverable_credential_support') ||
    combinedMessages.includes('discoverable credentials were required')
  ) {
    return 'Este dispositivo não suporta chaves de acesso salvas para login sem senha.'
  }

  if (
    combinedCodes.includes('error_authenticator_missing_user_verification_support') ||
    combinedCodes.includes('error_auto_register_user_verification_failure')
  ) {
    return 'Não foi possível verificar sua identidade com biometria ou PIN neste dispositivo.'
  }

  if (combinedCodes.includes('error_authenticator_general_error')) {
    return 'O autenticador do dispositivo não conseguiu concluir a operação. Tente novamente.'
  }

  if (combinedCodes.includes('error_authenticator_no_supported_pubkeycredparams_alg')) {
    return 'Este dispositivo não é compatível com o tipo de chave exigido.'
  }

  if (
    combinedCodes.includes('passkey_not_found') ||
    combinedNames.includes('invalidstateerror') ||
    combinedMessages.includes('no credentials') ||
    (combinedMessages.includes('credential') && combinedMessages.includes('not found'))
  ) {
    return 'Nenhuma chave de acesso encontrada neste dispositivo. Cadastre uma em Ajustes ou use email e senha.'
  }

  if (
    combinedCodes.includes('passkey_disabled') ||
    combinedMessages.includes('does not support webauthn') ||
    combinedMessages.includes('browser does not support webauthn')
  ) {
    return 'Este dispositivo/navegador não suporta chaves de acesso.'
  }

  if (combinedCodes.includes('email_not_confirmed') || combinedMessages.includes('email not confirmed')) {
    return 'Confirme seu email antes de cadastrar uma chave de acesso.'
  }

  if (
    combinedCodes.includes('anonymous_provider_disabled') ||
    combinedMessages.includes('anonymous') ||
    combinedMessages.includes('sso')
  ) {
    return 'Contas anônimas ou SSO não podem cadastrar chaves de acesso neste momento.'
  }

  if (
    combinedNames.includes('constrainterror') ||
    combinedMessages.includes('discoverable credentials were required')
  ) {
    return 'Este dispositivo não suporta chaves de acesso salvas para login sem senha.'
  }

  if (combinedMessages.includes('a non-webauthn related error has occurred')) {
    const causeName = ctx.names
      .map((name) => name.replace(/\s+/g, ''))
      .find((name) => name !== 'webauthnunknownerror' && name !== 'webauthnerror')
    if (causeName === 'invalidstateerror') {
      return 'Nenhuma chave de acesso encontrada neste dispositivo. Cadastre uma em Ajustes ou use email e senha.'
    }
    if (causeName === 'notsupportederror' || causeName === 'constrainterror') {
      return 'Este dispositivo/navegador não suporta chaves de acesso.'
    }
    if (causeName === 'securityerror') {
      return 'Chaves de acesso só funcionam em app.synap.med.br. Abra o Synap nesse endereço.'
    }
    if (causeName === 'typeerror') {
      return 'Não foi possível criar a chave de acesso neste navegador. Atualize o navegador ou tente em outro dispositivo.'
    }
    if (causeName === 'unknownerror') {
      return 'O autenticador do dispositivo não conseguiu concluir a operação. Tente novamente.'
    }
  }

  return fallback
}

const throwPasskeyError = (error: unknown, fallback: string, status = 400): never => {
  if (isPasskeyUserCancellation(error)) {
    throw new AppError('Operação cancelada.', {
      status,
      code: 'PASSKEY_CANCELLED',
      cause: error,
    })
  }

  throw new AppError(toPasskeyErrorMessage(error, fallback), {
    status,
    cause: error,
  })
}

const ensurePasskeySupported = async (): Promise<void> => {
  await ensureNativePasskeyBridge()
  if (!isPasskeyRpIdCompatible()) {
    throw new AppError(
      'Chaves de acesso só funcionam em app.synap.med.br. Abra o Synap nesse endereço.',
      { status: 400, code: 'PASSKEY_NOT_SUPPORTED' },
    )
  }
  const capable = await refreshPasskeyCapability()
  if (!capable || !isPasskeySupported()) {
    throw new AppError('Este dispositivo/navegador não suporta chaves de acesso.', {
      status: 400,
      code: 'PASSKEY_NOT_SUPPORTED',
    })
  }
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined

const readStringArray = (value: unknown): string[] | undefined =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    : undefined

const readBoolean = (value: unknown, fallback = false): boolean =>
  typeof value === 'boolean' ? value : fallback

const normalizeRole = (value: unknown): AuthUser['role'] =>
  value === 'doctor' || value === 'patient' || value === 'admin' ? value : 'patient'

const normalizePersonType = (value: unknown): 'pf' | 'pj' | undefined =>
  value === 'pf' || value === 'pj' ? value : undefined

const readGoogleDisplayName = (metadata: Record<string, unknown>): string | undefined =>
  readString(metadata.display_name) ??
  readString(metadata.name) ??
  readString(metadata.full_name)

const formatUser = (user: User): AuthUser => {
  const metadata = asRecord(user.user_metadata)
  const appMetadata = asRecord(user.app_metadata)
  const nameFromMetadata = readGoogleDisplayName(metadata)
  const emailFallback = user.email?.split('@')[0] ?? 'Usuário'

  return {
    id: user.id,
    name: nameFromMetadata ?? emailFallback,
    email: user.email ?? '',
    role: normalizeRole(metadata.role ?? appMetadata.role),
    crm: readString(metadata.crm),
    specialties: readStringArray(metadata.specialties) ?? readStringArray(metadata.especialidades),
    phone1: readString(metadata.phone1) ?? readString(metadata.telefone1),
    phone2: readString(metadata.phone2) ?? readString(metadata.telefone2),
    printName: readString(metadata.print_name) ?? readString(metadata.printName),
    contactEmail: readString(metadata.contact_email) ?? readString(metadata.contactEmail),
    personType: normalizePersonType(metadata.person_type ?? metadata.personType),
    cpfCnpj: readString(metadata.cpf_cnpj) ?? readString(metadata.cpfCnpj),
    pis: readString(metadata.pis),
    tradeName: readString(metadata.trade_name) ?? readString(metadata.tradeName),
    especialidades: readStringArray(metadata.especialidades) ?? readStringArray(metadata.specialties),
    telefone1: readString(metadata.telefone1) ?? readString(metadata.phone1),
    telefone2: readString(metadata.telefone2) ?? readString(metadata.phone2),
    avatarUrl: readString(metadata.avatar_url),
    isSystemAdmin: readBoolean(appMetadata.is_system_admin) || readBoolean(metadata.is_system_admin),
    onboardingCompleted: readBoolean(metadata.onboarding_completed),
    emailConfirmed: Boolean(user.email_confirmed_at),
  }
}

const requireCurrentUser = async (fallbackMessage = 'Sessão expirada. Faça login novamente.'): Promise<User> => {
  const { data: sessionData } = await supabase.auth.getSession()
  if (sessionData?.session?.user) return sessionData.session.user

  const { data, error } = await supabase.auth.getUser()
  if (error) throw toApiError(error, fallbackMessage, 401)
  if (!data.user) throw new AppError(fallbackMessage, { status: 401, code: 'UNAUTHORIZED' })
  return data.user
}

const refreshLegacyTokenFromCurrentSession = async () => {
  const { error } = await supabase.auth.getSession()
  if (error) {
    throw toApiError(error, 'Não foi possível validar a sessão atual.', 401)
  }
}


interface AuthResponse {
  user: AuthUser
}

export const authApi = {
  register: async (name: string, email: string, password: string): Promise<AuthResponse> => {
    ensureSupabaseConfigured()

    const cleanName = name.trim()
    const normalizedEmail = normalizeEmailInput(email)
    const redirectTo = getAuthRedirectTo('/confirm-email')
    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        emailRedirectTo: redirectTo,
        data: {
          display_name: cleanName,
          name: cleanName,
          role: 'patient',
          onboarding_completed: false,
        },
      },
    })

    if (error) {
      const friendlyMessage = toAuthErrorMessage(error, 'Não foi possível concluir o cadastro.')
      throw new AppError(friendlyMessage, {
        status: error.status ?? 500,
        cause: error,
      })
    }

    let user = data.user
    let session = data.session

    if (!session) {
      const signInAttempt = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      })

      if (signInAttempt.error) {
        throw new AppError(
          'Cadastro criado. Confirme seu email para concluir o acesso.',
          { status: 401, code: 'UNAUTHORIZED' },
        )
      }

      user = signInAttempt.data.user
      session = signInAttempt.data.session
    }

    if (!user) {
      throw new AppError('Não foi possível carregar o usuário cadastrado.', { status: 500 })
    }

    return { user: formatUser(user) }
  },

  signInWithGoogle: async (): Promise<void> => {
    ensureSupabaseConfigured()
    await signInWithGoogleOAuth()
  },

  finishOAuthCallback: async (searchParams: URLSearchParams): Promise<AuthResponse> => {
    ensureSupabaseConfigured()
    return finishOAuthCallbackImpl(searchParams, {
      requireCurrentUser,
      refreshLegacyTokenFromCurrentSession,
      formatUser,
    })
  },

  login: async (email: string, password: string): Promise<AuthResponse> => {
    ensureSupabaseConfigured()

    const normalizedEmail = normalizeEmailInput(email)
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      })

      if (error) {
        throw new AppError(toAuthErrorMessage(error, 'Email ou senha inválidos.'), {
          status: error.status ?? 401,
          code: 'UNAUTHORIZED',
          cause: error,
        })
      }

      if (!data.user) {
        throw new AppError('Usuário não encontrado para esta sessão.', {
          status: 401,
          code: 'UNAUTHORIZED',
        })
      }

      return { user: formatUser(data.user) }
    } catch (err) {
      const lockErr = err as { isAcquireTimeout?: boolean; message?: string }
      if (lockErr?.isAcquireTimeout) {
        throw new AppError(
          'A sessão está ocupada. Feche outras abas ou aguarde alguns segundos e tente novamente.',
          { status: 503, code: 'UNKNOWN', cause: err },
        )
      }
      throw err
    }
  },

  onboarding: async (data: OnboardingPayload): Promise<AuthResponse> => {
    ensureSupabaseConfigured()

    const { data: result, error } = await supabase.auth.updateUser({
      data: {
        role: data.role,
        onboarding_completed: true,
        crm: data.crm,
        especialidades: data.especialidades,
        telefone1: data.telefone1,
        telefone2: data.telefone2,
      },
    })

    if (error) {
      throw toApiError(error, 'Não foi possível concluir o onboarding.')
    }

    await refreshLegacyTokenFromCurrentSession()
    const user = result.user ?? (await requireCurrentUser())
    return { user: formatUser(user) }
  },

  confirmEmail: async (token: string): Promise<AuthResponse> => {
    ensureSupabaseConfigured()

    const tokenHash = token.trim()
    if (tokenHash) {
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'signup',
      })

      if (error) {
        throw toApiError(error, 'Token inválido ou já utilizado.', 400)
      }

      const user = data.user ?? (await requireCurrentUser('Não foi possível confirmar o email.'))
      return { user: formatUser(user) }
    }

    const user = await requireCurrentUser('Sessão não encontrada para confirmação de email.')
    await refreshLegacyTokenFromCurrentSession()
    return { user: formatUser(user) }
  },

  resendConfirmation: async (email: string): Promise<OkResponse> => {
    ensureSupabaseConfigured()

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: normalizeEmailInput(email),
      options: {
        emailRedirectTo: getAuthRedirectTo('/confirm-email'),
      },
    })

    if (error) {
      const friendlyMessage = toAuthErrorMessage(error, 'Não foi possível reenviar a confirmação de email.')
      throw new AppError(friendlyMessage, { status: error.status ?? 500, cause: error })
    }

    return { ok: true }
  },

  forgotPassword: async (email: string): Promise<OkResponse> => {
    ensureSupabaseConfigured()

    const { error } = await supabase.auth.resetPasswordForEmail(normalizeEmailInput(email), {
      redirectTo: getAuthRedirectTo('/reset-password'),
    })

    if (error) {
      const friendlyMessage = toAuthErrorMessage(error, 'Não foi possível enviar o link de recuperação.')
      throw new AppError(friendlyMessage, { status: error.status ?? 500, cause: error })
    }

    return { ok: true }
  },

  resetPassword: async (token: string, password: string): Promise<AuthResponse> => {
    ensureSupabaseConfigured()

    const tokenHash = token.trim()
    if (tokenHash) {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'recovery',
      })

      if (error) {
        throw toApiError(error, 'Link de redefinição inválido ou expirado.', 400)
      }

    } else {
      await refreshLegacyTokenFromCurrentSession()
    }

    const { data, error } = await supabase.auth.updateUser({ password })
    if (error) {
      throw toApiError(error, 'Não foi possível redefinir a senha.')
    }

    const user = data.user ?? (await requireCurrentUser('Não foi possível concluir a redefinição.'))
    await refreshLegacyTokenFromCurrentSession()
    return { user: formatUser(user) }
  },

  changePassword: async (email: string, currentPassword: string, newPassword: string): Promise<OkResponse> => {
    ensureSupabaseConfigured()

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password: currentPassword,
    })

    if (signInError) {
      throw new AppError('Senha atual incorreta. Verifique e tente novamente.', {
        status: 401,
        code: 'UNAUTHORIZED',
        cause: signInError,
      })
    }

    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword })
    if (updateError) {
      throw toApiError(updateError, 'Não foi possível alterar a senha.')
    }

    return { ok: true }
  },

  getMe: async (): Promise<{ user: AuthUser }> => {
    ensureSupabaseConfigured()
    /* Usar getUser (valida no servidor) para evitar sessão em cache após logout/login.
     * getSession pode retornar dados desatualizados; getUser garante sessão válida. */
    const { data, error } = await supabase.auth.getUser()
    if (!error && data?.user) {
      return { user: formatUser(data.user) }
    }
    const { error: refreshErr } = await supabase.auth.refreshSession()
    if (refreshErr) {
      throw toApiError(refreshErr, 'Sessão expirada. Faça login novamente.', 401)
    }
    const user = await requireCurrentUser()
    return { user: formatUser(user) }
  },

  updateProfile: async (data: {
    name?: string
    role?: string
    avatarUrl?: string
    crm?: string
    specialties?: string[]
    phone1?: string
    phone2?: string
    printName?: string
    contactEmail?: string
    personType?: 'pf' | 'pj'
    cpfCnpj?: string
    pis?: string
    tradeName?: string
    especialidades?: string[]
    telefone1?: string
    telefone2?: string
  }): Promise<AuthResponse> => {
    ensureSupabaseConfigured()

    const metadataUpdates: Record<string, unknown> = {}
    const normalizedName = data.name?.trim()
    if (normalizedName) {
      metadataUpdates.display_name = normalizedName
      metadataUpdates.name = normalizedName
    }
    // Avatar e logotipo ficam em profiles. Evita inflar user_metadata/JWT e quebrar a sessão.
    if (typeof data.role === 'string') {
      metadataUpdates.role = data.role
      metadataUpdates.onboarding_completed = true
    }
    if (typeof data.crm === 'string') metadataUpdates.crm = data.crm
    if (Array.isArray(data.specialties)) metadataUpdates.specialties = data.specialties
    if (typeof data.phone1 === 'string') metadataUpdates.phone1 = data.phone1
    if (typeof data.phone2 === 'string') metadataUpdates.phone2 = data.phone2
    if (typeof data.printName === 'string') metadataUpdates.print_name = data.printName
    if (typeof data.contactEmail === 'string') metadataUpdates.contact_email = data.contactEmail
    if (typeof data.personType === 'string') metadataUpdates.person_type = data.personType
    if (typeof data.cpfCnpj === 'string') metadataUpdates.cpf_cnpj = data.cpfCnpj
    if (typeof data.pis === 'string') metadataUpdates.pis = data.pis
    if (typeof data.tradeName === 'string') metadataUpdates.trade_name = data.tradeName
    if (Array.isArray(data.especialidades)) metadataUpdates.especialidades = data.especialidades
    if (typeof data.telefone1 === 'string') metadataUpdates.telefone1 = data.telefone1
    if (typeof data.telefone2 === 'string') metadataUpdates.telefone2 = data.telefone2

    if (!Object.keys(metadataUpdates).length) {
      return { user: (await authApi.getMe()).user }
    }

    const { data: result, error } = await supabase.auth.updateUser({
      data: metadataUpdates,
    })

    if (error) {
      throw toApiError(error, 'Não foi possível atualizar o perfil.')
    }

    await refreshLegacyTokenFromCurrentSession()
    const user = result.user ?? (await requireCurrentUser('Não foi possível carregar o perfil atualizado.'))
    return { user: formatUser(user) }
  },

  logout: async (): Promise<void> => {
    ensureSupabaseConfigured()
    try {
      const { pushSubscriptionsRepository } = await import(
        '@/shared/repositories/pushSubscriptions/pushSubscriptionsRepository'
      )
      await pushSubscriptionsRepository.removeAllTokensForCurrentUser()
    } catch {
      /* não bloqueia logout */
    }
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        throw toApiError(error, 'Não foi possível encerrar a sessão.')
      }
    } catch (err) {
      const lockErr = err as { isAcquireTimeout?: boolean }
      if (lockErr?.isAcquireTimeout) {
        /* Lock timeout: não bloquear; clearUserDataStores no finally garante estado limpo. */
        return
      }
      throw err
    }
  },

  /** Atualiza o token de sessão antes de operações sensíveis (ex.: importação em lote). */
  refreshSession: async (): Promise<void> => {
    ensureSupabaseConfigured()
    const { error } = await supabase.auth.refreshSession()
    if (error) {
      throw toApiError(error, 'Não foi possível atualizar a sessão. Faça login novamente.', 401)
    }
  },

  registerPasskey: async (friendlyName?: string): Promise<PasskeyRecord> => {
    ensureSupabaseConfigured()
    await ensurePasskeySupported()

    // Cerimônia em 2 passos: normaliza requireResidentKey (omitido pelo GoTrue) e classifica
    // ConstraintError/SecurityError corretamente — evita ERROR_PASSTHROUGH Non-Webauthn opaco.
    const { data: optionsPayload, error: optionsError } =
      await passkeyAuth().passkey.startRegistration()
    const registrationOptions = optionsPayload?.options
    const challengeId = optionsPayload?.challenge_id
    if (optionsError || !registrationOptions || !challengeId) {
      return throwPasskeyError(
        optionsError ?? new Error('Falha ao obter desafio de registro'),
        'Não foi possível cadastrar a chave de acesso.',
      )
    }

    const publicKey = deserializePasskeyCreationOptions(registrationOptions)
    const signal =
      typeof AbortController !== 'undefined' ? new AbortController().signal : undefined
    const { data: credential, error: credentialError } = await createPasskeyCredential(
      publicKey,
      signal,
    )
    if (credentialError || !credential) {
      return throwPasskeyError(
        credentialError ?? new Error('Cerimônia WebAuthn falhou'),
        'Não foi possível cadastrar a chave de acesso.',
      )
    }

    const { data, error } = await passkeyAuth().passkey.verifyRegistration({
      challengeId,
      credential: serializePasskeyCreationResponse(credential),
    })
    if (error) {
      return throwPasskeyError(error, 'Não foi possível cadastrar a chave de acesso.')
    }
    if (!data) {
      throw new AppError('Não foi possível cadastrar a chave de acesso.', { status: 500 })
    }

    const record = mapPasskeyRecord(data)
    const trimmedName = friendlyName?.trim()
    if (trimmedName) {
      await authApi.renamePasskey(record.id, trimmedName)
      return { ...record, friendlyName: trimmedName }
    }

    return record
  },

  signInWithPasskey: async (): Promise<AuthResponse> => {
    ensureSupabaseConfigured()
    await ensurePasskeySupported()

    try {
      // Limpa sessão local obsoleta antes da cerimônia discoverable (não exige email).
      await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined)

      const { data, error } = await passkeyAuth().signInWithPasskey()
      if (error) {
        throwPasskeyError(error, 'Não foi possível entrar com chave de acesso.', 401)
      }

      if (!data?.user) {
        throw new AppError('Usuário não encontrado para esta sessão.', {
          status: 401,
          code: 'UNAUTHORIZED',
        })
      }

      return { user: formatUser(data.user) }
    } catch (err) {
      if (err instanceof AppError) throw err
      const lockErr = err as { isAcquireTimeout?: boolean }
      if (lockErr?.isAcquireTimeout) {
        throw new AppError(
          'A sessão está ocupada. Feche outras abas ou aguarde alguns segundos e tente novamente.',
          { status: 503, code: 'UNKNOWN', cause: err },
        )
      }
      throw err
    }
  },

  listPasskeys: async (): Promise<PasskeyRecord[]> => {
    ensureSupabaseConfigured()

    const { data, error } = await passkeyAuth().passkey.list()
    if (error) {
      throwPasskeyError(error, 'Não foi possível listar as chaves de acesso.')
    }

    return (data ?? []).map(mapPasskeyRecord)
  },

  renamePasskey: async (id: string, friendlyName: string): Promise<void> => {
    ensureSupabaseConfigured()

    const trimmedName = friendlyName.trim()
    if (!trimmedName) {
      throw new AppError('Informe um nome para a chave de acesso.', { status: 400 })
    }

    const { error } = await passkeyAuth().passkey.update({
      passkeyId: id,
      friendlyName: trimmedName,
    })
    if (error) {
      throwPasskeyError(error, 'Não foi possível renomear a chave de acesso.')
    }
  },

  deletePasskey: async (id: string): Promise<void> => {
    ensureSupabaseConfigured()

    const { error } = await passkeyAuth().passkey.delete({ passkeyId: id })
    if (error) {
      throwPasskeyError(error, 'Não foi possível excluir a chave de acesso.')
    }
  },

  onAuthStateChange: (
    listener: (user: AuthUser | null, event: string) => void,
  ): (() => void) => {
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      const user = session?.user ? formatUser(session.user) : null
      listener(user, event)
    })

    return () => {
      data.subscription.unsubscribe()
    }
  },
}
